import camelCase from 'camelcase-keys-deep';
import union from 'lodash/union';
import uniqueId from 'lodash/uniqueId';
import { createReducer } from 'redux-act';
import * as actions from './actions';
import { fetchClaimsSuccess, checkSmdFileValiditySuccess, checkSmdFileValidityFail } from './domain/create/actions';
import { fetchClaimsSuccessHandler, checkSmdFileValidityHandler } from './domain/create/reducer';
import { fetchRenewDurationsSuccess } from './domain/renew/actions';
import fetchRenewDurationsSuccessHandler from './domain/renew/reducer';
import { fetchAuthInfosStart, fetchAuthInfosSuccess, fetchAuthInfosFail } from './domain/transfer/actions';
import { fetchAuthInfosStartHandler, fetchAuthInfosSuccessHandler, fetchAuthInfosFailHandler } from './domain/transfer/reducer';
import inAppPurchaseReducer, { initialState as inAppPurchaseInitialState } from './in-app-purchase';
import { resource as cartWidgetSuggestions, removeSuggestionOptimistic } from './suggest/actions';
import { fetchCartWidgetSuggestionsSuccessHandler, removeSuggestionOptimisticHandler } from './suggest/reducer';
import widgetReducer, { initialWidgetState } from './widget/reducers';
export const initialState = {
  grid: 'A',
  currency: '',
  unusedDiscounts: undefined,
  discounts: {},
  allDiscounts: [],
  products: {},
  contracts: {},
  isWorking: false,
  isLoaded: false,
  isFailure: false,
  isCommitting: false,
  isResuming: false,
  hasCommitError: false,
  isReopenable: false,
  isBeingCreated: false,
  total: {
    before_taxes: 0,
    after_taxes: 0,
    tax_amount: 0,
    taxes: [],
    discounts: []
  },
  status: 'notCreated',
  inAppPurchase: inAppPurchaseInitialState(),
  hasOrga: false,
  ownerList: {
    data: {},
    isFailure: false,
    isFetching: false,
    isLoaded: false
  },
  configurations: {
    data: [],
    isFailure: false,
    isFetching: false,
    isLoaded: false
  },
  bundles: undefined,
  widget: {
    visible: false,
    prices: true
  },
  widgetSuggestions: {}
};
const normalizeProductsKeys = product => {
  if ('priceUnit' in product) {
    product.unitPrice = product.priceUnit;
    delete product.priceUnit;
  }
  return product;
};
const resetCartHandler = () => initialState;
const createCartStartHandler = state => ({
  ...state,
  isBeingCreated: true
});
const setCartUUIDHandler = (state, cartUUID) => ({
  ...state,
  uuid: cartUUID
});
const setCartReselleeIdHandler = (state, reselleeId) => {
  if (reselleeId === state.reselleeId) {
    return state;
  }
  if (!reselleeId) {
    const {
      reselleeId: _,
      ...newState
    } = state;
    return newState;
  }
  return {
    ...state,
    reselleeId
  };
};
const setCartProductOwnerHandler = (state, productOwner) => {
  if (productOwner === state.productOwner) {
    return state;
  }
  if (!productOwner) {
    const {
      productOwner: _,
      ...newState
    } = state;
    return newState;
  }
  return {
    ...state,
    productOwner
  };
};
const setCartOwnerIsResellerHandler = (state, isOwnerReseller) => {
  if (isOwnerReseller === state.isOwnerReseller) {
    return state;
  }
  if (!isOwnerReseller) {
    const {
      isOwnerReseller: _,
      ...newState
    } = state;
    return newState;
  }
  return {
    ...state,
    isOwnerReseller
  };
};
const createCartSuccessHandler = (state, cartUUID) => ({
  ...state,
  ...setCartUUIDHandler(state, cartUUID),
  isBeingCreated: false,
  isFailure: false,
  status: 'active'
});
const createCartFailHandler = state => {
  const cart = {
    ...state,
    isBeingCreated: false
  };
  delete cart.uuid;
  return cart;
};
const updateCartStartHandler = state => {
  return {
    ...state,
    isWorking: true
  };
};
const updateCartSuccessHandler = state => {
  return {
    ...state,
    isWorking: false
  };
};
const updateCartFailHandler = state => {
  return {
    ...state,
    isWorking: false
  };
};
const getCartStartHandler = state => state.isWorking ? state : {
  ...state,
  isWorking: true
};
const getCartSuccessHandler = (state, responseData) => {
  const getProductErrors = product => {
    const errors = product.errors || [];
    if (product.prices?.length === 0 && (!errors || errors.length === 0)) {
      const priceError = {
        description: 'Price Not Found',
        name: 'Unprocessable Entity',
        location: `${product.product_type}/${product.process} service`
      };
      return [...errors, priceError];
    }
    return product.errors;
  };
  const products = (responseData.products || []).reduce((acc, product, i) => {
    const id = product.id;
    const previousProduct = state.products[id];
    const newProduct = {
      ...(previousProduct || {}),
      payload: product.payload || {},
      identifier: product.identifier,
      id: product.id,
      productType: product.product_type,
      isPremium: product.is_premium,
      isPromotion: product.is_promotion,
      discounts: product.discounts,
      process: product.process,
      prices: product.prices,
      status: product.status,
      errors: getProductErrors(product),
      options: product.options,
      total: product.total,
      bundleId: product.bundle_id,
      order: i
    };
    const newProductOptions = (newProduct?.options || []).map(option => normalizeProductsKeys(camelCase(option)));
    return {
      ...acc,
      [id]: {
        ...newProduct,
        options: newProductOptions
      }
    };
  }, {});
  const newContracts = (responseData.contracts || []).filter(c => {
    const identifier = c.identifier || c.href;
    return !Object.keys(state.contracts).includes(identifier);
  }).reduce((acc, contract, i) => {
    const identifier = contract.identifier ? contract.identifier : contract.href;
    return {
      ...acc,
      [identifier || `contract${i}`]: {
        ...contract,
        isApproved: false,
        order: i
      }
    };
  }, {});
  const previousContracts = (responseData.contracts || []).reduce((acc, c) => {
    const identifier = c.identifier || c.href;
    if (!!identifier && identifier in state.contracts) {
      acc[identifier] = state.contracts[identifier];
    }
    return acc;
  }, {});
  const discounts = (responseData.discounts || []).reduce((acc, discount) => {
    acc[String(discount.code)] = discount;
    return acc;
  }, {});
  const newState = {
    ...state,
    ...(responseData.owner_uuid ? {
      owner: responseData.owner_uuid
    } : {}),
    uuid: responseData.uuid,
    grid: responseData.grid_name,
    discounts,
    unusedDiscounts: responseData.unused_discounts,
    allDiscounts: responseData.discounts || [],
    contracts: {
      ...previousContracts,
      ...newContracts
    },
    currency: responseData.currency_iso,
    status: responseData.status,
    total: responseData.total || state.total,
    products,
    isWorking: false,
    isLoaded: true,
    isFailure: false,
    isCommitting: false,
    isReopenable: !!responseData.is_reopenable,
    hasCommitError: false,
    affiliate: responseData.affiliate,
    hasOrga: responseData.has_orga,
    bundles: responseData.bundles,
    productOwner: responseData.product_owner_uuid,
    referer: responseData.referer
  };
  const reselleeId = responseData.products.find(product => product.payload.resellee_id)?.payload.resellee_id;
  return setCartReselleeIdHandler(newState, reselleeId);
};
const setCartStatusHandler = (state, status) => ({
  ...state,
  status
});
const getCartFailHandler = (state, status) => ({
  ...state,
  ...setCartStatusHandler(state, status),
  isWorking: false,
  isLoaded: false,
  isFailure: true
});
const fetchCartOwnerListStartHandler = state => ({
  ...state,
  ownerList: {
    ...state.ownerList,
    isFetching: true
  }
});
const fetchCartOwnerListSuccessHandler = (state, payload) => ({
  ...state,
  ownerList: {
    ...state.ownerList,
    data: payload,
    isFailure: false,
    isFetching: false,
    isLoaded: true
  }
});
const fetchCartOwnerListFailHandler = (state, statusCode) => ({
  ...state,
  ownerList: {
    ...state.ownerList,
    isFailure: statusCode ?? true,
    isFetching: false
  }
});
const fetchCartConfigurationsStartHandler = state => ({
  ...state,
  configurations: {
    ...state.configurations,
    isFetching: true
  }
});
const fetchCartConfigurationsSuccessHandler = (state, payload) => ({
  ...state,
  configurations: {
    ...state.configurations,
    data: payload,
    isFailure: false,
    isFetching: false,
    isLoaded: true
  }
});
const fetchCartConfigurationsFailHandler = state => ({
  ...state,
  configurations: {
    ...state.configurations,
    isFailure: true,
    isFetching: false
  }
});
const commitCartStartHandler = state => ({
  ...state,
  isCommitting: true,
  hasCommitError: false
});
const commitCartSuccessHandler = state => ({
  ...state,
  status: 'closed',
  isCommitting: false,
  hasCommitError: false,
  isReopenable: true
});
const commitCartFailHandler = state => ({
  ...state,
  isCommitting: false,
  hasCommitError: true,
  isReopenable: false
});
const resumeCartStartHandler = state => ({
  ...state,
  isResuming: true
});
const resumeCartSuccessHandler = state => ({
  ...state,
  isResuming: false,
  status: 'active'
});
const resumeCartFailHandler = state => ({
  ...state,
  isResuming: false,
  status: 'closed'
});
const addProductStartHandler = state => ({
  ...state,
  isWorking: true
});
const addProductSuccessHandler = (state, {
  productData,
  apiResults,
  extra
}) => {
  const order = Object.keys(state.products).length;
  const previousOptimisticID = extra.optimisticId;
  const newProduct = {
    ...(previousOptimisticID ? state.products[previousOptimisticID] : {}),
    id: apiResults.id,
    identifier: productData.identifier,
    productType: productData.product_type,
    process: productData.process,
    payload: productData.payload,
    bundleId: productData.bundle_id,
    prices: apiResults.prices,
    status: apiResults.status,
    errors: apiResults.errors,
    total: apiResults.total,
    isPremium: apiResults.is_premium,
    isPromotion: apiResults.is_promotion,
    isWorking: false,
    pending: false,
    order,
    ...extra
  };
  const previousProducts = {
    ...state.products
  };
  if (previousOptimisticID && previousProducts[previousOptimisticID]) {
    delete previousProducts[previousOptimisticID];
  }
  return {
    ...state,
    products: {
      ...previousProducts,
      [apiResults.id]: normalizeProductsKeys(newProduct)
    },
    isWorking: false
  };
};
const addProductOptimisticHandler = (state, optimisticProduct) => {
  const order = state.products ? Object.keys(state.products).length : 0;
  const total = {
    before_taxes: (state.total.before_taxes || 0) + optimisticProduct.priceBeforeTaxes,
    after_taxes: (state.total.after_taxes || 0) + optimisticProduct.priceAfterTaxes,
    taxes: union(state.total.taxes || [], optimisticProduct.taxes || [])
  };
  const id = optimisticProduct.id || uniqueId('optimistic-');
  return {
    ...state,
    currency: state.currency || optimisticProduct.currency,
    total,
    products: {
      ...state.products,
      [id]: normalizeProductsKeys({
        ...optimisticProduct,
        id,
        status: 'optimistic',
        errors: [],
        order
      })
    },
    isWorking: false
  };
};
const addProductFailHandler = state => ({
  ...state,
  isWorking: false
});
const addProductsStartHandler = state => ({
  ...state,
  isWorking: true
});
const addProductsFailHandler = state => ({
  ...state,
  isWorking: false
});
const updateProductStartHandler = (state, productId) => ({
  ...state,
  products: {
    ...state.products,
    [productId]: normalizeProductsKeys({
      ...state.products[productId],
      isWorking: true
    })
  },
  isWorking: true
});
const updateProductSuccessHandler = (state, {
  productId,
  payload,
  apiResults
}) => {
  return {
    ...state,
    products: {
      ...state.products,
      [productId]: normalizeProductsKeys({
        ...state.products[productId],
        payload,
        prices: apiResults.prices,
        status: apiResults.status,
        errors: apiResults.errors,
        isWorking: false
      })
    },
    isWorking: false
  };
};
const updateProductFailHandler = (state, productId) => ({
  ...state,
  products: {
    ...state.products,
    [productId]: normalizeProductsKeys({
      ...state.products[productId],
      isWorking: false
    })
  },
  isWorking: false
});
const updateProductsStartHandler = state => ({
  ...state,
  isWorking: true
});
const updateProductsFailHandler = state => ({
  ...state,
  isWorking: false
});
const removeProductStartHandler = (state, productId) => ({
  ...state,
  isWorking: true,
  products: {
    ...state.products,
    [productId]: normalizeProductsKeys({
      ...state.products[productId],
      isWorking: true
    })
  }
});
const removeProductOptimisticHandler = (state, productId) => {
  const productToRemove = state.products[productId];
  if (!productToRemove) {
    throw new Error(`Cannot find product ${productId}. Cannot remove.`);
  }
  const newTotalAfterTaxes = state.total.after_taxes - (productToRemove.total && productToRemove.total.after_taxes || Number(productToRemove.priceAfterTaxes) || 0);
  const newTotalBeforeTaxes = state.total.before_taxes - (productToRemove.total && productToRemove.total.before_taxes || Number(productToRemove.priceBeforeTaxes) || 0);
  const total = {
    ...state.total,
    after_taxes: !Number.isNaN(newTotalAfterTaxes) ? newTotalAfterTaxes : 0,
    before_taxes: !Number.isNaN(newTotalBeforeTaxes) ? newTotalBeforeTaxes : 0
  };
  const products = {
    ...state.products
  };
  delete products[productId];
  return {
    ...state,
    total,
    products
  };
};
const removeProductSuccessHandler = state => {
  return {
    ...state,
    isWorking: false
  };
};
const removeProductFailHandler = (state, productId) => ({
  ...state,
  isWorking: false,
  products: {
    ...state.products,
    [productId]: normalizeProductsKeys({
      ...state.products[productId],
      isWorking: false
    })
  }
});
const removeProductsStartHandler = state => ({
  ...state,
  isWorking: true
});
const removeProductsFailHandler = state => ({
  ...state,
  isWorking: false
});
const addDiscountStartHandler = state => ({
  ...state,
  isWorking: true
});
const addDiscountSuccessHandler = (state, {
  discountData,
  apiResults
}) => {
  return {
    ...state,
    discounts: {
      ...state.discounts,
      [String(discountData.code)]: {
        code: discountData.code,
        id: apiResults.id
      }
    },
    allDiscounts: [...state.allDiscounts, discountData],
    isWorking: false
  };
};
const addDiscountFailHandler = state => ({
  ...state,
  isWorking: false
});
const removeDiscountStartHandler = state => ({
  ...state,
  isWorking: true
});
const removeDiscountSuccessHandler = (state, discountCodeOrId) => {
  const discountToRemove = state.discounts[`${discountCodeOrId}`] || state.allDiscounts.find(discount => discount.code === discountCodeOrId || discount.id === discountCodeOrId);
  if (!discountToRemove) {
    return state;
  }
  const discounts = {
    ...state.discounts
  };
  delete discounts[discountCodeOrId];
  const allDiscounts = state.allDiscounts.filter(discount => discount.code !== discountCodeOrId && discount.id !== discountCodeOrId);
  const unusedDiscounts = state.unusedDiscounts && state.unusedDiscounts.filter(discount => discount.id !== discountToRemove.id);
  return {
    ...state,
    discounts,
    allDiscounts,
    unusedDiscounts
  };
};
const removeDiscountFailHandler = state => ({
  ...state,
  isWorking: false
});
const callCatalogStartHandler = (state, productId) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          catalog: []
        })
      }
    };
  }
  return state;
};
const callCatalogSuccessHandler = (state, {
  productId,
  apiResults
}) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          catalog: apiResults
        })
      }
    };
  }
  return state;
};
const callCatalogPendingHandler = (state, productId) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          catalog: [],
          pending: true
        })
      }
    };
  }
  return state;
};
const callCatalogFailHandler = (state, productId) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId]
        })
      }
    };
  }
  return state;
};
const startPollingCatalogHandler = (state, {
  productId,
  pollerId
}) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          pollerId
        })
      }
    };
  }
  return state;
};
const removeCatalogPollerHandler = (state, productId) => {
  if (state.products[productId]) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          pollerId: undefined,
          pending: false
        })
      }
    };
  }
  return state;
};
const approveContractHandler = (state, {
  identifier,
  isApproved
}) => {
  return {
    ...state,
    contracts: {
      ...state.contracts,
      [identifier]: {
        ...state.contracts[identifier],
        isApproved: !!isApproved
      }
    }
  };
};
const unsetProductsWorkingStatusHandler = state => {
  const products = Object.keys(state.products).reduce((acc, identifier) => {
    const product = state.products[identifier];
    return {
      ...acc,
      [identifier]: {
        ...product,
        isWorking: false,
        pending: false
      }
    };
  }, {});
  return {
    ...state,
    products
  };
};
const mergeCartHandler = (state, toMerge = {}) => ({
  ...state,
  ...toMerge,
  products: normalizeProductsKeys(state.products)
});
const mergeProductHandler = (state, {
  productId,
  data
}) => {
  const product = state.products[productId];
  if (product) {
    return {
      ...state,
      products: {
        ...state.products,
        [productId]: normalizeProductsKeys({
          ...state.products[productId],
          ...data,
          payload: state.products[productId].payload
        })
      }
    };
  }
  return state;
};
const defaultReducer = createReducer({
  [actions.mergeCart]: mergeCartHandler,
  [actions.mergeProduct]: mergeProductHandler,
  [actions.setCartUUID]: setCartUUIDHandler,
  [actions.resetCart]: resetCartHandler,
  [actions.setCartReselleeId]: setCartReselleeIdHandler,
  [actions.setCartProductOwner]: setCartProductOwnerHandler,
  [actions.setCartOwnerIsReseller]: setCartOwnerIsResellerHandler,
  [actions.createCartStart]: createCartStartHandler,
  [actions.createCartSuccess]: createCartSuccessHandler,
  [actions.createCartFail]: createCartFailHandler,
  [actions.updateCartStart]: updateCartStartHandler,
  [actions.updateCartSuccess]: updateCartSuccessHandler,
  [actions.updateCartFail]: updateCartFailHandler,
  [actions.getCartStart]: getCartStartHandler,
  [actions.getCartSuccess]: getCartSuccessHandler,
  [actions.getCartFail.getType()]: getCartFailHandler,
  [actions.fetchCartOwnerListStart]: fetchCartOwnerListStartHandler,
  [actions.fetchCartOwnerListSuccess]: fetchCartOwnerListSuccessHandler,
  [actions.fetchCartOwnerListFail]: fetchCartOwnerListFailHandler,
  [actions.fetchCartConfigurationsStart]: fetchCartConfigurationsStartHandler,
  [actions.fetchCartConfigurationsSuccess]: fetchCartConfigurationsSuccessHandler,
  [actions.fetchCartConfigurationsFail]: fetchCartConfigurationsFailHandler,
  [actions.setCartStatus]: setCartStatusHandler,
  [actions.commitCartStart]: commitCartStartHandler,
  [actions.commitCartSuccess]: commitCartSuccessHandler,
  [actions.commitCartFail]: commitCartFailHandler,
  [actions.resumeCartStart]: resumeCartStartHandler,
  [actions.resumeCartSuccess]: resumeCartSuccessHandler,
  [actions.resumeCartFail]: resumeCartFailHandler,
  [actions.addProductStart]: addProductStartHandler,
  [actions.addProductSuccess.getType()]: addProductSuccessHandler,
  [actions.addProductFail]: addProductFailHandler,
  [actions.addProductsStart]: addProductsStartHandler,
  [actions.addProductsFail]: addProductsFailHandler,
  [actions.addProductOptimistic]: addProductOptimisticHandler,
  [actions.removeProductOptimistic]: removeProductOptimisticHandler,
  [actions.updateProductStart]: updateProductStartHandler,
  [actions.updateProductSuccess.getType()]: updateProductSuccessHandler,
  [actions.updateProductFail]: updateProductFailHandler,
  [actions.updateProductsStart]: updateProductsStartHandler,
  [actions.updateProductsFail]: updateProductsFailHandler,
  [actions.removeProductStart]: removeProductStartHandler,
  [actions.removeProductSuccess]: removeProductSuccessHandler,
  [actions.removeProductFail]: removeProductFailHandler,
  [actions.removeProductsStart]: removeProductsStartHandler,
  [actions.removeProductsFail]: removeProductsFailHandler,
  [actions.callDomainCatalogStart]: callCatalogStartHandler,
  [actions.callDomainCatalogSuccess.getType()]: callCatalogSuccessHandler,
  [actions.callDomainCatalogFail.getType()]: callCatalogFailHandler,
  [actions.callDomainCatalogPending.getType()]: callCatalogPendingHandler,
  [actions.addDiscountStart]: addDiscountStartHandler,
  [actions.addDiscountSuccess.getType()]: addDiscountSuccessHandler,
  [actions.addDiscountFail]: addDiscountFailHandler,
  [actions.removeDiscountStart]: removeDiscountStartHandler,
  [actions.removeDiscountSuccess]: removeDiscountSuccessHandler,
  [actions.removeDiscountFail]: removeDiscountFailHandler,
  [actions.startPollingDomainCatalog.getType()]: startPollingCatalogHandler,
  [actions.removeDomainCatalogPoller.getType()]: removeCatalogPollerHandler,
  [actions.approveContract]: approveContractHandler,
  [actions.unsetProductsWorkingStatus]: unsetProductsWorkingStatusHandler,
  [fetchClaimsSuccess]: fetchClaimsSuccessHandler,
  [checkSmdFileValiditySuccess]: checkSmdFileValidityHandler,
  [checkSmdFileValidityFail]: checkSmdFileValidityHandler,
  [fetchAuthInfosStart]: fetchAuthInfosStartHandler,
  [fetchAuthInfosSuccess]: fetchAuthInfosSuccessHandler,
  [fetchAuthInfosFail]: fetchAuthInfosFailHandler,
  [fetchRenewDurationsSuccess]: fetchRenewDurationsSuccessHandler,
  [cartWidgetSuggestions.fetch.success.getType()]: fetchCartWidgetSuggestionsSuccessHandler,
  [removeSuggestionOptimistic]: removeSuggestionOptimisticHandler
}, initialState);
export default (state, action) => ({
  ...defaultReducer(state, action),
  inAppPurchase: inAppPurchaseReducer(state ? state.inAppPurchase : undefined, action),
  widget: widgetReducer(state ? state.widget : initialWidgetState, action)
});